import {HttpStatusCode} from '@angular/common/http';
import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    DeletedBaseType,
    EntityType,
    IncidentApiService,
    IncidentResponse,
    IncidentType,
    InquiriesResponse,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {NESTED_TAB, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {EMPTY, mergeMap, Observable, of, zip} from 'rxjs';
import {DeleteDialogComponent, DeleteDialogData} from '../../../components/delete-dialog/delete-dialog.component';
import {mapIncidentStatusToStyle, StatusStyle} from '../../../components/status/status.component';
import {TableEvent} from '../../../components/full-table/full-table.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../domain/dictionary-holder.service';
import {INCIDENT_ID, TRANSACTION_ID} from '../../../domain/query-params.keys';
import {Path} from '../../../path';
import {IncidentsTab} from '../incidents.component';
import {
    AnalyticAgentReportDialogComponent,
    AnalyticAgentReportDialogData,
} from './analytic-agent-report-dialog/analytic-agent-report-dialog.component';
import {DisplayRowItem, MAX_LENGTH_TO_CROP} from './info/info.component';

@Component({
    templateUrl: 'incident-details.component.html',
    styleUrls: ['incident-details.component.scss'],
    standalone: false,
})
export class IncidentDetailsComponent {
    EntityType = EntityType;
    incident: IncidentResponse;
    statusStyle: StatusStyle;
    inquiries: InquiriesResponse[];
    unreadInquiriesCount: number;
    incidentId: string;
    transactionId: string;
    info: DisplayRowItem[][] = [];
    tabs: IncidentDetailsTab[] = [];
    selectedTabIndex: number;
    showButton: boolean;
    deletedBaseName: string;
    isRestrictedAccess: boolean;
    activeTab: IncidentDetailsTab;
    private deletedBaseDictionary: ReferenceBookResponse[];
    private organizations: ReferenceBookResponse[];
    private backToTab: IncidentsTab;

    constructor(
        public authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private incidentApiService: IncidentApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private dialogService: DialogService,
        private snackBarService: SnackbarService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.paramMap.get(INCIDENT_ID);
        this.transactionId = this.activatedRoute.snapshot.queryParamMap.get(TRANSACTION_ID);
        this.backToTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as IncidentsTab;
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB) as IncidentDetailsTab || 'tabIncidentDetails';
        zip([
            this.incidentApiService.getIncident(this.incidentId),
            this.dictionaryHolderService.getReference('bin'),
            this.incidentApiService.getIncidentInquiries(this.incidentId),
            this.dictionaryHolderService.getReference(ReferenceBookType.DELETED_BASED),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe({
                next: ([response, organizations, inquiries, deletedBaseDictionary]) => {
                    this.organizations = organizations;
                    this.deletedBaseDictionary = deletedBaseDictionary;
                    this.inquiries = inquiries;
                    this.unreadInquiriesCount = this.inquiries.map(item => item.items)
                        .reduce((acc, val) => acc.concat(val), [])
                        .filter(item => item.inquiry.isUnread || item.response?.isUnread)
                        .length;
                    this.setIncident(response);
                },
                error: error => {
                    if (error.status === HttpStatusCode.Forbidden) {
                        this.routerService.navigateToTab<IncidentsTab>(Path.INCIDENTS, this.backToTab);
                        this.snackBarService.showSnackBar('i18n_af_snackBarMessageIncidentAccessRestricted', 'error', false,
                            {incidentId: this.incidentId});
                        return;
                    }
                    throw error;
                },
            });
    }

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.routerService.navigate(
            `${Path.INCIDENTS}/${this.incidentId}`,
            {
                queryParams: {
                    [TAB]: this.backToTab,
                    [NESTED_TAB]: this.activeTab,
                },
                queryParamsHandling: 'merge',
            });
    }

    navigateBack = (): Observable<boolean> => {
        this.routerService.navigateToTab<IncidentsTab>(Path.INCIDENTS, this.backToTab);
        return of(false);
    };

    editIncident(): void {
        this.routerService.navigate(Path.INCIDENTS_EDIT, {
                pathParams: {[INCIDENT_ID]: this.incidentId},
                queryParams: {
                    [TAB]: this.backToTab,
                    [NESTED_TAB]: this.activeTab,
                },
            },
        );
    }

    deleteIncident(): void {
        this.dialogService.openCustomDialog<DeleteDialogComponent, {comment: string; reason: DeletedBaseType | null}>(
            DeleteDialogComponent,
            {
                id: this.incidentId,
                title: 'i18n_af_deleteIncidentDialogTitle',
                subtitle: 'i18n_af_deleteIncidentDialogSubtitle',
                showReason: true,
                reasonFilter: ['incident', 'all'],
            } as DeleteDialogData,
        )
            .pipe(
                mergeMap(result => {
                    if (!result) {
                        return EMPTY;
                    }
                    const normalized: {comment: string; reason: DeletedBaseType | null} =
                        typeof result === 'string' ? {comment: result, reason: null} : result;
                    return this.incidentApiService.deleteIncident(
                        this.incidentId,
                        normalized.comment,
                        normalized.reason,
                    );
                }),
            )
            .subscribe(() => {
                this.snackBarService
                    .showSnackBar('i18n_af_snackBarMessageIncidentDeleted', 'success', false, {incidentId: this.incidentId});
                this.routerService.navigateToTab<IncidentsTab>(Path.INCIDENTS, this.backToTab);
            });
    }

    generateAnalyticReport = (): Observable<AnalyticAgentReportDialogData> => {
        return this.dialogService.openCustomDialog<AnalyticAgentReportDialogComponent, AnalyticAgentReportDialogData>(
            AnalyticAgentReportDialogComponent,
            <AnalyticAgentReportDialogData>{incidentId: this.incidentId}, {disableClose: true}
        );
    };

    goToResolution(): void {
        this.routerService.navigate(Path.INCIDENTS_RESOLUTION, {
            pathParams: {[INCIDENT_ID]: this.incidentId},
            queryParams: {
                [TAB]: this.backToTab,
                [NESTED_TAB]: this.activeTab,
            },
        });
    }

    reloadIncident(): void {
        this.incidentApiService.getIncident(this.incidentId)
            .subscribe(response => this.setIncident(response));
    }

    navigateToCreateRefunds(): void {
        this.routerService.navigate(Path.INCIDENTS_CREATE_REFUND, {
            pathParams: {
                [INCIDENT_ID]: this.incident.id,
            },
            queryParams: {
                [TAB]: this.backToTab,
                [NESTED_TAB]: this.activeTab,
            },
        });
    }

    private setIncident(response: IncidentResponse): void {
        this.incident = response;
        this.isRestrictedAccess = this.hasRestrictedAccess(response);
        const typeTabs = TABS_BY_INCIDENT_TYPE[response.typeCode as IncidentType];
        this.tabs = this.resolveTabs(typeTabs);
        this.selectedTabIndex = Math.max(0, this.tabs.findIndex(tab => tab === this.activeTab));
        this.statusStyle = mapIncidentStatusToStyle(response.incidentStatus);
        this.showButton = this.authHolderService.hasAccess(['government-user'])
            && this.hasResolutionAccess(response)
            && !response.isInnocentStatus
            && !response.isDeleted
            && response.typeCode !== IncidentType.MSISDN_FRAUD;
        if (response.isDeleted) {
            this.deletedBaseName = findValueInDictionary(this.deletedBaseDictionary, response.deletedBase, 'name_ru');
        }
        this.buildIncidentInfo(response);
    }

    private hasResolutionAccess(response: IncidentResponse): boolean {
        const organizationBin = this.authHolderService.organizationBin;
        const userOrganization = findValueInDictionary(this.organizations, organizationBin, 'name_ru');
        return response.created_by_organization === organizationBin
            || (response.related ?? []).includes(userOrganization)
            || response.governmentBin === organizationBin;
    }

    private hasRestrictedAccess(response: IncidentResponse): boolean {
        if (!this.authHolderService.hasAccess(['finance-user'])) {
            return false;
        }
        const userBin = this.authHolderService.organizationBin;
        if (response.created_by_organization === userBin) {
            return false;
        }
        if (response.governmentBin === userBin) {
            return false;
        }
        const userOrgName = findValueInDictionary(this.organizations, userBin, 'name_ru');
        return !(response.related ?? []).includes(userOrgName);
    }

    private resolveTabs(typeTabs: IncidentDetailsTab[]): IncidentDetailsTab[] {
        if (this.isRestrictedAccess) {
            return typeTabs.filter(tab => RESTRICTED_ACCESS_TABS.includes(tab));
        }
        if (this.authHolderService.hasAccess(['mobile-operator-user'])) {
            return typeTabs.filter(tab => tab !== 'tabRefunds');
        }
        if (this.authHolderService.hasAccess(['finance-user'])) {
            return typeTabs.filter(tab => tab !== 'tabAnalyticAgentReports');
        }
        return [...typeTabs];
    }

    private buildIncidentInfo(response: IncidentResponse): void {
        const govOrganization = this.organizations.filter(org => org.code1 === 'IO')
            .find(organization => response.governmentBin === organization.code || response.related.includes(organization.name_ru))
            ?.name_ru;
        const related = response.related.filter(organization => organization !== govOrganization);
        const firstRow: DisplayRowItem[] = [
            {
                type: 'priority',
                label: 'priority',
                value: response.priority,
            },
            {
                type: 'status',
                label: 'resolution',
                value: response.resolutionStatus,
                statusStyle: mapIncidentStatusToStyle(response.resolutionStatus),
            },
            !!response.amount && {
                type: 'money',
                label: 'amount',
                value: response.amount,
            },
            {
                type: 'text',
                label: 'source_organization',
                value: response.source_organization,
                cropText: response.source_organization.length > MAX_LENGTH_TO_CROP,
            },
            {
                type: 'text',
                label: 'source_author',
                value: response.source_author,
                cropText: response.source_author.length > MAX_LENGTH_TO_CROP,
            },
            {
                type: 'date-time',
                label: 'created_date',
                value: response.created_date,
            },
        ];
        const secondRow: DisplayRowItem[] = [
            {
                type: 'text',
                label: 'fraud_type',
                value: response.type,
            },
            {
                type: 'text',
                label: 'fraud_sub_type',
                value: response.fraud_type,
                cropText: response.fraud_type.length > MAX_LENGTH_TO_CROP,
            },
        ];
        govOrganization && secondRow.push({
            type: 'text',
            label: 'govOrganization',
            value: govOrganization,
            cropText: govOrganization.length > MAX_LENGTH_TO_CROP,
            span: 1,
        });
        const relatedOrganizations = related.join(', ');
        secondRow.push({
            type: 'text',
            label: 'related',
            value: relatedOrganizations,
            span: govOrganization ? 2 : 3,
            cropText: relatedOrganizations.length > MAX_LENGTH_TO_CROP,
        });
        this.info = [firstRow.filter(val => !!val), secondRow];
    }
}

export declare type IncidentDetailsTab =
    'tabIncidentDetails'
    | 'tabContacts'
    | 'tabMsisdnFraudEntries'
    | 'tabDocuments'
    | 'tabTransactions'
    | 'tabCompromisedPaymentInstrument'
    | 'tabResolutions'
    | 'tabRequests'
    | 'tabHistory'
    | 'tabRefunds'
    | 'tabAnalyticAgentReports';

const RESTRICTED_ACCESS_TABS: IncidentDetailsTab[] = ['tabIncidentDetails', 'tabResolutions', 'tabHistory'];

const TABS_BY_INCIDENT_TYPE: Record<IncidentType, IncidentDetailsTab[]> = {
    [IncidentType.FRAUD]: [
        'tabIncidentDetails',
        'tabContacts',
        'tabDocuments',
        'tabTransactions',
        'tabResolutions',
        'tabRequests',
        'tabRefunds',
        'tabHistory',
        'tabAnalyticAgentReports',
    ],
    [IncidentType.OTHER]: [
        'tabIncidentDetails',
        'tabDocuments',
        'tabTransactions',
        'tabResolutions',
        'tabRequests',
        'tabRefunds',
        'tabHistory',
    ],
    [IncidentType.COMPROMISED_PAYMENT_INSTRUMENT]: [
        'tabIncidentDetails',
        'tabDocuments',
        'tabCompromisedPaymentInstrument',
        'tabResolutions',
        'tabRequests',
        'tabRefunds',
        'tabHistory',
    ],
    [IncidentType.MSISDN_FRAUD]: [
        'tabIncidentDetails',
        'tabMsisdnFraudEntries',
        'tabDocuments',
        'tabHistory',
    ],
};

export interface IncidentDetailsTabState {
    tableEvent: TableEvent;
}
